import { fdmcaPart1DataSchema } from "../../schemas/mhaForms/mca/fdmca.js";
import { fdmcaAvtSections } from "./fdmcaAvtSections.js";
export const fdmcaPart1Template = {
    signing: {
        userOrTeamRequirement: {
            accreditation: ["doctor", "nurse", "hcpc", "social-worker"],
            teamType: [],
        },
    },
    formBuilderFields: [
        {
            type: "readonly",
            label: "Name and address of person",
            textFn: (data) => [
                data.patient?.name || "",
                [data.patient?.address, data.patient?.postalCode]
                    .filter(Boolean)
                    .join(", "),
            ],
            isReadOnlyPatientInfo: true,
        },
        {
            type: "practitioner",
            label: "Name and address of person completing this form",
            field: "user",
        },
        {
            type: "label",
            bannerType: "info",
            label: [
                "This form may be used where:\n• the person is not detained under a provision to which Part 4 of the Mental Health Act applies\n• the decision concerns care or treatment that is not treatment for mental disorder\n• you are establishing capacity and consent for the purposes of the section 58 safeguards at the three month point\n• you are establishing capacity and consent for the purposes of the section 58A safeguards for ECT, which apply from the start of detention rather than at the three month point\nDo not use this form as an alternative to the Part 4 treatment provisions. Where Part 4 applies, treatment for mental disorder is authorised under the Mental Health Act and the statutory certificate process must be followed.",
            ],
        },
        {
            type: "textbox",
            field: "decisionOutline",
            label: "Outline the decision to be made",
            rows: "5",
        },
        {
            type: "textbox",
            field: "supportSteps",
            label: "Describe the steps taken to support the person to make the decision, including any reasonable adjustments.",
            rows: "5",
        },
        {
            type: "textbox",
            field: "supportingPeople",
            label: "List the people who supported the person while their capacity was being assessed (e.g. family carer, IMCA, learning disability liaison nurse).",
            rows: "5",
        },
        {
            type: "textbox",
            field: "materialTime",
            label: "What is the material time for this capacity assessment?",
            rows: "3",
        },
        {
            type: "label",
            bannerType: "info",
            label: [
                "You are assessing capacity to make a specific decision at the material time (for example a single issue/one-off decision, or a longitudinal/prolonged period of care and treatment in hospital).",
            ],
        },
        {
            type: "textbox",
            field: "additionalDecisionInformation",
            label: "Record any information relevant to the decision being made",
            rows: "5",
        },
        {
            type: "label",
            bannerType: "info",
            label: [
                "The questions below should be answered by reference to the decision, the relevant information, the material time, and the support described above.",
            ],
        },
        {
            type: "branch",
            field: "canUnderstandInformation",
            label: "Is the person unable to understand the relevant information needed to make the decision?",
            branches: [
                { fieldValue: "able", label: "Able", fields: [], fieldsOwned: [] },
                { fieldValue: "unable", label: "Unable", fields: [], fieldsOwned: [] },
            ],
        },
        {
            type: "label",
            bannerType: "info",
            label: [
                "Consider whether you have provided the information in a way the person can understand. Consider whether the person understands the consequences of deciding one way or another, or of not making a decision.",
            ],
        },
        {
            type: "textbox",
            field: "reasoningUnderstand",
            label: "Record reasoning",
            rows: "5",
        },
        {
            type: "branch",
            field: "canRetainInformation",
            label: "Is the person unable to retain the relevant information long enough to make the decision?",
            branches: [
                { fieldValue: "able", label: "Able", fields: [], fieldsOwned: [] },
                { fieldValue: "unable", label: "Unable", fields: [], fieldsOwned: [] },
            ],
        },
        {
            type: "label",
            bannerType: "info",
            label: [
                "Consider whether the person can remember the information long enough to make the decision.\nRetention for a short period does not prevent the ability to make a decision.",
            ],
        },
        {
            type: "textbox",
            field: "reasoningRetain",
            label: "Record reasoning",
            rows: "5",
        },
        {
            type: "branch",
            field: "canUseAndWeighInformation",
            label: "Is the person unable to use or weigh the relevant information as part of making the decision?",
            branches: [
                { fieldValue: "able", label: "Able", fields: [], fieldsOwned: [] },
                { fieldValue: "unable", label: "Unable", fields: [], fieldsOwned: [] },
            ],
        },
        {
            type: "textbox",
            field: "reasoningUseAndWeigh",
            label: "Record reasoning",
            rows: "5",
        },
        {
            type: "branch",
            field: "canCommunicateDecision",
            label: "Is the person unable to communicate their decision, for example, simple language, visual aids or any other means?",
            branches: [
                { fieldValue: "able", label: "Able", fields: [], fieldsOwned: [] },
                { fieldValue: "unable", label: "Unable", fields: [], fieldsOwned: [] },
            ],
        },
        {
            type: "textbox",
            field: "reasoningCommunicate",
            label: "Record reasoning",
            rows: "5",
        },
        {
            type: "label",
            bannerType: "info",
            label: [
                "If you answered 'Unable' to any of the four questions above, complete the questions below. It doesn't matter if the impairment or disturbance is temporary or permanent. For example, is there a reasonable suggestion that this person might have dementia, delirium, a mental illness, stress or severe emotional difficulties?",
            ],
        },
        {
            type: "branch",
            field: "impairmentDisturbance",
            label: "Do you consider that the person's inability to make the decision is because of an impairment or disturbance in the functioning of the mind or brain?",
            branches: [
                {
                    fieldValue: "yes",
                    label: "Yes",
                    fieldsOwned: ["impairmentType"],
                    fields: [
                        {
                            type: "textbox",
                            field: "impairmentType",
                            label: "What impairment do they appear to have?",
                            rows: "5",
                        },
                    ],
                },
                { fieldValue: "no", label: "No", fields: [], fieldsOwned: [] },
            ],
        },
        {
            type: "branch",
            field: "lackOfCapacity",
            label: "Based on your answers above, have you determined a lack of capacity to make the decision at this time?",
            branches: [
                {
                    fieldValue: "has-capacity",
                    label: "The person is able to make this decision",
                    fields: [],
                    fieldsOwned: [],
                },
                {
                    fieldValue: "lacks-capacity",
                    label: "The person lacks capacity to make this decision",
                    fields: [],
                    fieldsOwned: [],
                },
            ],
        },
        {
            type: "label",
            bannerType: "info",
            label: [
                "A person is not to be treated as unable to make a decision merely because they make an unwise decision.",
            ],
        },
        {
            type: "textbox",
            field: "reasoningLackOfCapacity",
            label: "Record reasoning",
            rows: "5",
        },
    ],
    avtSections: fdmcaAvtSections,
    dataSchema: fdmcaPart1DataSchema,
};
export const fdmcaV1Base = {
    id: "mca-fdmca",
    category: "mca",
    version: "1.0",
    status: "active",
    formName: "FDMCA",
    section: "",
    parts: [fdmcaPart1Template],
    description: "Functional and Diagnostic Mental Capacity Assessment",
    displayTitle: "Functional and Diagnostic Mental Capacity Assessment",
    extractGenericData: () => ({}),
};
//# sourceMappingURL=fdmca.js.map